import React from 'react';
import { AIAnnotationStructuredDataType, CadastralSurveyAIAnnotationCategory, DocumentTypeValue } from '../../../domain/types';
import {
  CadastralRegistry, Document, PropertyUnit,
} from '../../../providers/api/dtos';
import { arePropertyUnitsPartialMatch } from '../../utils';
import { isEqual } from '../../../utils/object/object';

export type SuggestedUnitStatus = 'new' | 'partial_match';

interface NewSuggestedPropertyUnit {
  suggestion: PropertyUnit,
  status: 'new',
}

interface PartialMatchSuggestedPropertyUnit {
  suggestion: PropertyUnit,
  existing: PropertyUnit,
  status: 'partial_match',
}

export type SuggestedPropertyUnit = NewSuggestedPropertyUnit | PartialMatchSuggestedPropertyUnit;

export interface AiSuggestedCadastralRegistry {
  locality?: string,
  suggestedUnits: SuggestedPropertyUnit[],
}

interface CadastralRegistryAware {
  cadastralRegistry?: CadastralRegistry,
}

export const useAiSuggestedCadastralRegistry = (
  cadastralRegistryAware?: CadastralRegistryAware,
  aiAnalysiedDocument?: Document,
): AiSuggestedCadastralRegistry | undefined => React.useMemo(() => {
  if (aiAnalysiedDocument?.type !== DocumentTypeValue.CADASTRAL_SURVEY) return undefined;

  const allCadastralStructuredData = aiAnalysiedDocument.files.flatMap((file) => (file.analysisResult?.annotations ?? [])
    .filter((annotation) => annotation.category === CadastralSurveyAIAnnotationCategory.CADASTRAL_INFO)
    .flatMap((annotation) => annotation.structuredData ?? [])
    .filter((structuredData) => structuredData.type === AIAnnotationStructuredDataType.CADASTRAL_REGISTRY));

  const locality = allCadastralStructuredData
    .map((structuredData) => structuredData.data.locality)
    .find((l) => l !== undefined);

  const suggestedUnits = allCadastralStructuredData
    .flatMap((structuredData) => structuredData.data.units ?? [])
    .filter((unit, index, self) => index === self.findIndex((u) => isEqual(u, unit, { loose: true })))
    .flatMap((unit): SuggestedPropertyUnit[] => {
      const existingUnit = cadastralRegistryAware?.cadastralRegistry?.units?.find(
        (u) => isEqual(u, unit, { loose: true }) || arePropertyUnitsPartialMatch(u, unit),
      );

      if (!existingUnit) {
        return [{ suggestion: unit, status: 'new' }];
      }

      if (isEqual(existingUnit, unit, { loose: true })) {
        return [];
      }

      return [{
        suggestion: unit, status: 'partial_match', existing: existingUnit,
      }];
    });

  if (suggestedUnits.length === 0 && !locality) return undefined;

  return { locality, suggestedUnits };
}, [aiAnalysiedDocument?.files, aiAnalysiedDocument?.type, cadastralRegistryAware?.cadastralRegistry?.units]);
